/**
 * MediaRenderer - Handles rendering of different media types
 *
 * @module MediaRenderer
 */

'use strict';

const PATTERNS = {
    image  :/\.(jpg|jpeg|png|gif|webp|svg|bmp|ico|avif)$/i,
    video  :/\.(mp4|webm|ogg|mov|avi)$/i,
    youtube:/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/i,
    vimeo  :/vimeo\.com\/(?:video\/)?(\d+)/i,
    vkvideo:/vkvideo\.ru\/video-?(\d+)_([\d\w]+)/i,
    vkshort:/vk\.com\/video-?(\d+)_([\d\w]+)/i,
    rutube :/rutube\.ru\/video\/(\w{32})/i
};

/**
 * Media Renderer Class
 */
export class MediaRenderer {
    /**
     * Create renderer instance
     * @param {MediaConfig} config - Plugin configuration
     */
    constructor(config) {
        this.config = config;
        this.sizeCache = new Map();
    }

    /**
     * Detect media type from URL
     * @param {string} url - Media URL
     * @returns {string} Media type
     */
    detectMediaType(url) {
        if (PATTERNS.image.test(url)) return 'image';
        if (PATTERNS.video.test(url)) return 'video';
        if (PATTERNS.youtube.test(url)) return 'iframe';
        if (PATTERNS.vimeo.test(url)) return 'iframe';
        if (PATTERNS.vkvideo.test(url)) return 'iframe';
        if (PATTERNS.vkshort.test(url)) return 'iframe';
        if (PATTERNS.rutube.test(url)) return 'iframe';

        return 'iframe';
    }

    /**
     * Process iframe URL (add autoplay for videos)
     * @param {string} url - Original iframe URL
     * @param {MediaData} mediaData - Media data
     * @returns {string} Processed iframe URL
     */
    processIframeUrl(url, mediaData) {
        if (PATTERNS.youtube.test(url)) {
            const match = url.match(PATTERNS.youtube);
            if (match && match[1]) {
                const videoId  = match[1];
                const autoplay = mediaData.autoplay ? '&autoplay=1' : '';
                return `https://www.youtube.com/embed/${videoId}?${autoplay}`;
            }
        }

        if (PATTERNS.vimeo.test(url)) {
            const match = url.match(PATTERNS.vimeo);
            if (match && match[1]) {
                const videoId  = match[1];
                const autoplay = mediaData.autoplay ? '&autoplay=1' : '';
                return `https://player.vimeo.com/video/${videoId}?${autoplay}`;
            }
        }

        if (PATTERNS.vkvideo.test(url) || PATTERNS.vkshort.test(url)) {
            const match = url.match(PATTERNS.vkvideo) || url.match(PATTERNS.vkshort);
            if (match && match[1] && match[2]) {
                const ownerId  = match[1];
                const videoId  = match[2];
                const autoplay = mediaData.autoplay ? '&autoplay=1' : '';
                return `https://vkvideo.ru/video_ext.php?oid=-${ownerId}&id=${videoId}&hd=2${autoplay}`;
            }
        }

        if (PATTERNS.rutube.test(url)) {
            const match = url.match(PATTERNS.rutube);
            if (match && match[1]) {
                const videoId  = match[1];
                const autoplay = mediaData.autoplay ? '?autoplay=1' : '';
                return `https://rutube.ru/play/embed/${videoId}${autoplay}`;
            }
        }

        return url;
    }

    /**
     * Render media content based on type
     * @param {MediaData} mediaData - Media data object
     * @returns {string} HTML string
     */
    renderMedia(mediaData) {
        const unsupportedText = (this.config.locales && this.config.locales.unsupported) ? this.config.locales.unsupported : 'Unsupported media type';

        switch (mediaData.type) {
            case 'image':
                return this.renderImage(mediaData);

            case 'video':
                return `<video ${this.config.controls ? 'controls' : ''}
                                   ${mediaData.autoplay ? 'autoplay' : ''}
                                   ${mediaData.loop ? 'loop' : ''}
                                   preload="${this.config.preload}"
                                   class="bm-media-video">
                                <source src="${mediaData.url}" type="video/mp4">
                                ${unsupportedText}
                            </video>`;

            case 'iframe':
                const iframeSrc = this.processIframeUrl(mediaData.url, mediaData);
                return `<iframe src="${iframeSrc}"
                                    width="${mediaData.iframeWidth}"
                                    height="${mediaData.iframeHeight}"
                                    allow="${mediaData.iframeAllow}"
                                    loading="${this.config.loading}"
                                    allowfullscreen
                                    class="bm-media-iframe"></iframe>`;

            default:
                return `<p>${unsupportedText}: ${mediaData.type}</p>`;
        }
    }

    /**
     * Render image with optional intrinsic sizes
     * @param {MediaData} mediaData - Media data object
     * @returns {string} HTML string
     * @private
     */
    renderImage(mediaData) {
        const widthAttr  = mediaData.width ? ` width="${mediaData.width}"` : '';
        const heightAttr = mediaData.height ? ` height="${mediaData.height}"` : '';
        const aspect     = mediaData.width && mediaData.height ? ` style="aspect-ratio: ${mediaData.width} / ${mediaData.height};"` : '';

        return `<img src="${mediaData.url}"
                         alt="${this.escapeHtml(mediaData.title)}"
                         loading="${this.config.loading}"
                         class="bm-media-image"
                         ${widthAttr}${heightAttr}${aspect}
                         onload="this.classList.add('bm-media-image--loaded')">`;
    }

    /**
     * Ensure image dimensions are known before render
     * @param {MediaData} mediaData - Media data object
     * @returns {Promise<void>}
     */
    async ensureImageSize(mediaData) {
        if (mediaData.type !== 'image') {
            return;
        }

        if (mediaData.width && mediaData.height) {
            return;
        }

        const cached = this.sizeCache.get(mediaData.url);
        if (cached) {
            mediaData.width = cached.width;
            mediaData.height = cached.height;
            return;
        }

        try {
            const size = await this.loadImageSize(mediaData.url);
            if (size) {
                mediaData.width = size.width;
                mediaData.height = size.height;
                this.sizeCache.set(mediaData.url, size);
            }
        } catch (error) {
            if (this.config.debugMode) {
                console.warn('MediaRenderer: cannot load image size', mediaData.url, error);
            }
        }
    }

    /**
     * Load image size via Image object
     * @param {string} url - Image URL
     * @returns {Promise<{width:number,height:number}>}
     * @private
     */
    loadImageSize(url) {
        return new Promise((resolve, reject) => {
            const img = new Image();
            img.onload = () => {
                resolve({ width: img.naturalWidth, height: img.naturalHeight });
            };
            img.onerror = (err) => reject(err);
            img.src = url;
        });
    }

    /**
     * Escape HTML to prevent XSS
     * @param {string} text - Text to escape
     * @returns {string} Escaped HTML
     */
    escapeHtml(text) {
        const div       = document.createElement('div');
        div.textContent = text;
        return div.innerHTML;
    }
}

export default MediaRenderer;
